import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {
  combineLatest,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { AnalyticsService } from '../analytics/analytics.service';
import { AuthActions } from '../auth/store/auth.actions';
import { ConnectorsActions } from '../connectors/connectors.actions';
import { connectorsFeature } from '../connectors/connectors.reducer';
import { FeatureFlagsActions, featureFlagsFeature } from '../feature-flags';
import { LayoutActions } from '../layout/store/layout.actions';
import { layoutFeature } from '../layout/store/layout.reducer';
import {
  selectAvailableSettingsPanes,
  selectConnectorsAvailability,
} from './settings.selectors';

/** The settings window's side of the connectors list and of the pane flags (SB-6520). */
@Injectable()
export class SettingsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);
  private readonly analyticsService = inject(AnalyticsService);

  /**
   * Opening settings learns whether Connectors can show, and every request aimed at Connectors
   * re-reads statuses (3.1, 3.3). Only with `ff-mcp-connectors` on, so someone without connectors
   * never meets a connectors error (KD-12).
   */
  loadConnectors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LayoutActions.openSettings, LayoutActions.selectSettingsPane),
      withLatestFrom(
        this.store.select(
          featureFlagsFeature.selectFeatureFlag<boolean>('ff-mcp-connectors'),
        ),
        this.store.select(connectorsFeature.selectConnectorsState),
      ),
      filter(
        ([{ pane }, enabled, state]) =>
          !!enabled &&
          (pane === 'connectors' || (!state.loaded && !state.isLoading)),
      ),
      map(() => ConnectorsActions.loadConnectors()),
    ),
  );

  /**
   * Settings opened before the signed-in user's flags loaded skipped the load above when the
   * anonymous flag read off: load once the user's own flags say Connectors are on, so a sign-in
   * return waiting on them resolves (KD-12).
   */
  loadConnectorsOnceFlagsReady$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeatureFlagsActions.userIdentified),
      withLatestFrom(
        this.store.select(
          featureFlagsFeature.selectFeatureFlag<boolean>('ff-mcp-connectors'),
        ),
        this.store.select(layoutFeature.selectSettings),
        this.store.select(connectorsFeature.selectConnectorsState),
      ),
      filter(
        ([, enabled, settings, state]) =>
          !!enabled && settings.isOpen && !state.loaded && !state.isLoading,
      ),
      map(() => ConnectorsActions.loadConnectors()),
    ),
  );

  /**
   * A request for Connectors waits, unseen, until the first load resolves: it opens once they are
   * available and is dropped if they never are, so nothing opens an empty pane (3.8, 3.9).
   */
  resolveConnectorsRequest$ = createEffect(() =>
    combineLatest([
      this.store.select(layoutFeature.selectSettings),
      this.store.select(selectConnectorsAvailability),
    ]).pipe(
      filter(
        ([settings, availability]) =>
          settings.awaitingConnectors && availability !== 'pending',
      ),
      map(([, availability]) =>
        availability === 'available'
          ? LayoutActions.settingsConnectorsResolved()
          : LayoutActions.closeSettings(),
      ),
    ),
  );

  /** A pane that goes away while selected (its flag turned off) leaves Preferences selected (1.6). */
  fallBack$ = createEffect(() =>
    combineLatest([
      this.store.select(layoutFeature.selectSettings),
      this.store.select(selectAvailableSettingsPanes),
    ]).pipe(
      filter(
        ([settings, available]) =>
          settings.isOpen &&
          !settings.awaitingConnectors &&
          settings.selectedPane !== null &&
          !available.includes(settings.selectedPane),
      ),
      map(() => LayoutActions.settingsFallBack()),
    ),
  );

  /** The memory switch saved: memory_toggled, only after success (KD-7). */
  memoryToggled$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.updateMemoryEnabledSuccess),
        switchMap(({ user }) =>
          this.analyticsService.sendMemoryToggledEvent({
            enabled: user.memoryEnabled,
          }),
        ),
      ),
    { dispatch: false },
  );

  /** The switch is already back where it was (auth reducer); say why (spec 5.4). */
  memoryToggleFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.updateMemoryEnabledFailure),
        tap(() =>
          this.toastrService.error(
            this.translateService.instant(
              'SETTINGS.MEMORY.ERRORS.TOGGLE',
            ) as string,
          ),
        ),
      ),
    { dispatch: false },
  );
}
